import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { debounce } from "lodash";

import queryString from "query-string";
import { IoFunnel } from "react-icons/io5";

import ProjectService from "../../../services/projectService";

import ProjectListFilters from "./ProjectListFilters";
import ProjectList from "./ProjectList";

import HeaderWrapper from "../../common/HeaderWrapper";

import "./ProjectListPage.scss";

import {
  getFilterState,
  saveFilterState,
  customToast as toast
} from "../../../utils";

const propTypes = {
  auth: PropTypes.object,
  history: PropTypes.object,
  dash: PropTypes.bool,
  isMobile: PropTypes.bool,
  onPageSizeChange: PropTypes.func,
  dragState: PropTypes.object
};
const defaultProps = {
  dash: false,
  onPageSizeChange: () => null,
  dragState: {}
};

const ProjectListPage = ({
  auth,
  history,
  dash,
  isMobile,
  onPageSizeChange,
  dragState
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState(
    dash
      ? {
          page: 0,
          pageSize: 10,
          favorites: auth.user.favorites
        }
      : getFilterState("PROJECT_FILTERS", { page: 0, pageSize: 50 })
  );

  const [sorted, setSorted] = useState(
    getFilterState("PROJECT_SORTED", [{ id: "start_date", desc: false }])
  );

  const [pages, setPages] = useState(1);
  const [showFilters, setShowFilters] = useState(true);
  const [checked, setChecked] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const newSorted = sorted.reduce((acc, curr) => {
        acc[`sort.${curr.id}`] = curr.desc ? 1 : -1;
        return acc;
      }, {});

      const query = queryString.stringify(
        {
          ...filters,
          ...newSorted,
          onlyFavorites: dash ? true : checked,
          dash
        },
        {
          arrayFormat: "bracket"
        }
      );
      const {
        data: result,
        pages: numPages,
        total: count
      } = await ProjectService.get(`/list?${query}`);
      setData(result);
      setPages(numPages);
      setTotal(count);
      onPageSizeChange(filters.pageSize, numPages, result.length);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, sorted, onPageSizeChange, checked, dash]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPages(0);
    if (!dash) {
      saveFilterState("PROJECT_FILTERS", filters);
    }
  }, [filters, dash]);

  useEffect(() => {
    if (!dash) {
      saveFilterState("PROJECT_SORTED", sorted);
    }
  }, [sorted, dash]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // fetchData();
  };

  const handleChange = (name) => (value) => {
    // favorites use separate function below
    if (name === "department" && !value?.length) {
      setFilters((prev) => ({
        ...prev,
        [name]: [],
        department_category: []
      }));
    }
    if (Array.isArray(value)) {
      setFilters((prev) => ({
        ...prev,
        page: 0,
        [name]: value.map((v) => v.value || v)
      }));
    } else {
      const newFilters = Object.keys(filters).reduce((acc, curr) => {
        if (curr !== name) {
          acc[curr] = filters[curr];
        }
        return acc;
      }, {});
      setFilters({ ...newFilters, page: 0 });
    }
  };

  const handleToggle = (e) => {
    setChecked(!!e.target.checked);
    const toggle = e.target.checked;

    setFilters((prev) => ({
      ...prev,
      page: 0,
      favorites: toggle ? auth.user.favorites : null
    }));
  };

  const handleSearchChange = debounce((search) => {
    setFilters((prev) => ({ ...prev, page: 0, search }));
  }, 1000);

  const handleSortedChange = (rtSorted) => {
    setSorted(rtSorted);
  };

  const handlePageSizeChange = (pageSize) => {
    setFilters((prev) => ({ ...prev, pageSize }));
  };
  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleReset = () => {
    setChecked(false);
    setFilters({
      pageSize: dash ? 10 : 50,
      department: [],
      project_manager: [],
      category: [],
      call_type: [],
      project_status: [],
      customer: []
    });
  };

  const handleAddProject = () => {
    history.push("/app/projects/add");
  };
  const handleRowClick = (rowInfo) => {
    if (!dash || (dash && !dragState.dragInProcess && dragState.dragOver)) {
      history.push(`/app/projects/${rowInfo.original._id}`);
    }
  };

  return dash ? (
    <div className="report-page">
      <ProjectList
        data={data}
        loading={loading}
        sorted={sorted}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortedChange={handleSortedChange}
        page={filters.page}
        pages={pages}
        pageSize={filters.pageSize}
        onRowClick={handleRowClick}
        isMobile={isMobile}
        isFavoriteToggable={!dragState.dragInProcess && dragState.dragOver}
      />
    </div>
  ) : (
    <div className="report-page">
      <HeaderWrapper
        breadcrumbs={[{ label: `Projects (${total} projects)` }]}
        onClickAdd={auth.maxRole > 2000 && handleAddProject}
        onClickAction={[
          { action: () => setShowFilters(!showFilters), icon: <IoFunnel /> }
        ]}
      />

      <ProjectListFilters
        show={showFilters}
        filters={filters}
        handleChange={handleChange}
        handleReset={handleReset}
        handleToggle={handleToggle}
        checked={checked}
        loading={loading}
        handleSearchChange={handleSearchChange}
        handleSubmit={handleSubmit}
        favorites={auth.user.favorites}
      />

      <ProjectList
        data={data}
        loading={loading}
        sorted={sorted}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortedChange={handleSortedChange}
        page={filters.page}
        pages={pages}
        pageSize={filters.pageSize}
        onRowClick={handleRowClick}
        isMobile={isMobile}
      />
    </div>
  );
};

ProjectListPage.propTypes = propTypes;
ProjectListPage.defaultProps = defaultProps;

function mapStatesToProps(state) {
  return {
    auth: state.reducers.auth
  };
}
export default connect(mapStatesToProps)(ProjectListPage);
